import { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../hooks/useAuth";
import { OrganizationSwitcher } from "../organizations/OrganizationSwitcher";
import { LanguageSwitcher } from "../common/LanguageSwitcher";
import { CheckIcon, ChevronDownIcon, CloseIcon, LogoIcon, LogoutIcon, MenuIcon } from "../icons";
import styles from "./Header.module.css";

export function Header() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    }

    if (isUserMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isUserMenuOpen]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <header className={styles.appHeader}>
      <div className={styles.headerContainer}>
        <div className={styles.headerLeft}>
          <Link to="/dashboard" className={styles.logo}>
            <LogoIcon />
            <span className={styles.logoText}>{t("common.appName")}</span>
          </Link>

          <nav className={styles.desktopNav}>
            <Link to="/dashboard" className={styles.navLink}>
              {t("navigation.dashboard")}
            </Link>
          </nav>
        </div>

        <div className={styles.headerRight}>
          <div className={styles.desktopControls}>
            <LanguageSwitcher variant="header" />
            <OrganizationSwitcher />

            <div className={styles.userMenu} ref={userMenuRef}>
              <button
                className={styles.userMenuButton}
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                aria-expanded={isUserMenuOpen}
                aria-haspopup="true"
              >
                <div className={styles.userAvatar}>{user.email.charAt(0).toUpperCase()}</div>
                <span className={styles.userEmail}>{user.email}</span>
                <ChevronDownIcon
                  className={`${styles.chevron} ${isUserMenuOpen ? styles.chevronRotate : ""}`}
                />
              </button>

              {isUserMenuOpen && (
                <div className={styles.userMenuDropdown}>
                  <div className={styles.userInfo}>
                    <div className={styles.userInfoEmail}>{user.email}</div>
                    {user.emailVerified && (
                      <div className={styles.verifiedBadge}>
                        <CheckIcon />
                        {t("auth.verified")}
                      </div>
                    )}
                  </div>
                  <div className={styles.menuDivider}></div>
                  <button
                    className={`${styles.menuItem} ${styles.menuItemLogout}`}
                    onClick={handleLogout}
                  >
                    <LogoutIcon />
                    {t("auth.logout")}
                  </button>
                </div>
              )}
            </div>
          </div>

          <button
            className={styles.mobileMenuButton}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className={styles.mobileMenu}>
          <nav className={styles.mobileNav}>
            <Link
              to="/dashboard"
              className={styles.mobileNavLink}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {t("navigation.dashboard")}
            </Link>
          </nav>
          <div className={styles.mobileMenuFooter}>
            <div className={styles.mobileLanguageSwitcher}>
              <LanguageSwitcher variant="header" />
            </div>
            <div className={styles.mobileUserInfo}>
              <div className={styles.userAvatar}>{user.email.charAt(0).toUpperCase()}</div>
              <div className={styles.userDetails}>
                <div className={styles.userEmailMobile}>{user.email}</div>
                {user.emailVerified && (
                  <div className={styles.verifiedBadgeMobile}>{t("auth.verified")}</div>
                )}
              </div>
            </div>
            <button className={styles.mobileLogout} onClick={handleLogout}>
              {t("auth.logout")}
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
